/**
 *
 * ShiftDetailCardApplicationStatus
 *
 */

import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';

import EditIcon from '@mui/icons-material/Edit';
import DotIcon from '@mui/icons-material/FiberManualRecord';
import PublicIcon from '@mui/icons-material/Public';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';

import { yellow, green, cyan } from '@mui/material/colors';

import Spacings from '~/modules/ui-kit/spacings';
import ConfirmationModal from '~/components/ConfirmationModal';
import { useReleaseShift } from '~/hooks/api/useReleaseShift';
import { useFreezeShift } from '~/hooks/api/useFreezeShift';
import { useRevokeShift } from '~/hooks/api/useRevokeShift';
import { useDeleteShift } from '~/hooks/api/useDeleteShift';
import { PRIVATE, RELEASED, FROZEN } from './constants';
import messages from './messages';
import styles from './styles.module.css';
import { getRouteApi, useNavigate } from '@tanstack/react-router';

const route = getRouteApi('/weekplan/$year/$week/$day/{-$shiftId}');

const getColorForState = (state: string) => {
  switch (state) {
    case PRIVATE:
      return yellow[800];
    case RELEASED:
      return green[800];
    case FROZEN:
      return cyan[300];
    default:
      return 'black';
  }
};

type ShiftDetailCardApplicationStatusProps = {
  state: string;
  shiftHasApplications: boolean;
  shiftId: number;
};

export const ShiftDetailCardApplicationStatus = (
  props: ShiftDetailCardApplicationStatusProps
) => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const params = route.useParams();
  const navigate = useNavigate();
  const [shouldShowReleaseDialog, setShouldShowReleaseDialog] =
    React.useState(false);
  const releaseShiftMutation = useReleaseShift({
    onError: () => {
      showSnackbar({
        message: intl.formatMessage(messages.releaseFailed),
      });
    },
    onSuccess: () => {
      showSnackbar({
        message: intl.formatMessage(messages.releaseSuccess),
      });
    },
    onSettled: () => {
      setShouldShowReleaseDialog(false);
    },
  });
  const freezeShiftMutation = useFreezeShift({
    onError: () => {
      showSnackbar({
        message: intl.formatMessage(messages.freezeFailure),
      });
    },
    onSuccess: () => {
      showSnackbar({
        message: intl.formatMessage(messages.freezeSuccess),
      });
    },
  });
  const revokeShiftMutation = useRevokeShift({
    onError: () => {
      showSnackbar({
        message: intl.formatMessage(messages.revokeFailure),
      });
    },
    onSuccess: () => {
      showSnackbar({
        message: intl.formatMessage(messages.revokeSuccess),
      });
    },
  });
  const deleteShiftMutation = useDeleteShift({
    onError: () => {
      showSnackbar({
        message: intl.formatMessage(messages.deleteFailure),
      });
    },
    onSuccess: () => {
      showSnackbar({
        message: intl.formatMessage(messages.deleteSuccess),
      });
      navigate({
        to: '/weekplan/$year/$week/$day/{-$shiftId}',
        params: {
          ...params,
          shiftId: undefined,
        },
      });
    },
  });
  return (
    <Card>
      <Typography variant="subtitle2" style={{ padding: 16 }}>
        <FormattedMessage {...messages.title} />
      </Typography>
      <div className={styles.status}>
        <DotIcon
          data-testid="dot-icon"
          style={{ color: getColorForState(props.state) }}
        />
        <CardHeader
          title={intl.formatMessage(
            messages[props.state.toLowerCase() as keyof typeof messages]
          )}
          subheader={intl.formatMessage(
            messages[
              `${props.state.toLowerCase()}Description` as keyof typeof messages
            ]
          )}
          style={{ paddingTop: 0, paddingBottom: 0 }}
        />
      </div>
      <div>
        <CardActions style={{ padding: 16, paddingTop: 0 }}>
          <Spacings.Inline>
            {props.state === PRIVATE && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PublicIcon />}
                onClick={() => setShouldShowReleaseDialog(true)}
              >
                {intl.formatMessage(messages.release)}
              </Button>
            )}
            {props.state === PRIVATE && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<DeleteIcon />}
                style={{ marginLeft: 16 }}
                onClick={() =>
                  deleteShiftMutation.mutate({ id: props.shiftId })
                }
              >
                {intl.formatMessage(messages.delete)}
              </Button>
            )}
            {props.state === RELEASED && !props.shiftHasApplications && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={() =>
                  revokeShiftMutation.mutate({ id: props.shiftId })
                }
              >
                {intl.formatMessage(messages.revoke)}
              </Button>
            )}
            {props.state === RELEASED && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<BlockIcon />}
                onClick={() =>
                  freezeShiftMutation.mutate({ id: props.shiftId })
                }
              >
                {intl.formatMessage(messages.freeze)}
              </Button>
            )}
            {props.state === FROZEN && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PublicIcon />}
                onClick={() => setShouldShowReleaseDialog(true)}
              >
                {intl.formatMessage(messages.thaw)}
              </Button>
            )}
          </Spacings.Inline>
        </CardActions>

        <ConfirmationModal
          open={shouldShowReleaseDialog}
          title={intl.formatMessage(messages.releaseConfirmTitle)}
          text={<FormattedMessage {...messages.releaseConfirmText} />}
          confirmLabel={<FormattedMessage {...messages.releaseConfirmButton} />}
          cancelLabel={<FormattedMessage {...messages.releaseConfirmCancel} />}
          onCancel={() => setShouldShowReleaseDialog(false)}
          onConfirm={() => releaseShiftMutation.mutate({ id: props.shiftId })}
        />
      </div>
    </Card>
  );
};

export default ShiftDetailCardApplicationStatus;
